import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Phone,
  ClipboardCheck,
  Users,
  CheckCircle,
  FileText,
  AlertCircle,
  Star,
  Send,
} from 'lucide-react';
import { SEO } from '../../../shared/components';

// --- Data Structures ---
interface EnrollmentStep {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface DocumentItem {
  text: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface Testimonial {
  name: string;
  role: string;
  text: string;
  initials: string;
}

const EnrollmentPage: React.FC = () => {
  const [formData, setFormData] = useState({
    studentName: '',
    parentName: '',
    email: '',
    phone: '',
    grade: '',
    curriculum: '',
    subjects: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Dummy submission - simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));

    console.log('Form submitted:', formData);
    alert('Thank you for your enquiry! We will contact you soon.');

    // Reset form
    setFormData({
      studentName: '',
      parentName: '',
      email: '',
      phone: '',
      grade: '',
      curriculum: '',
      subjects: '',
      message: '',
    });
    setIsSubmitting(false);
  };

  const enrollmentSteps: EnrollmentStep[] = [
    {
      icon: <Phone className="w-8 h-8 text-accent" />,
      title: 'Inquiry',
      description: 'Contact us via phone, email, or our website to express your interest and ask any initial questions about our programs.',
    },
    {
      icon: <ClipboardCheck className="w-8 h-8 text-accent" />,
      title: 'Assessment',
      description: "We conduct a brief academic assessment to understand the student's current level, strengths, and areas for improvement.",
    },
    {
      icon: <Users className="w-8 h-8 text-accent" />,
      title: 'Consultation',
      description: 'Meet with our academic team to discuss the assessment results and create a personalized learning plan.',
    },
    {
      icon: <CheckCircle className="w-8 h-8 text-accent" />,
      title: 'Enrollment',
      description: 'Complete the registration process, choose your schedule, and begin your educational journey with us.',
    },
  ];

  const studentDocuments: DocumentItem[] = [
    { text: 'Completed enrollment form' },
    { text: "Copy of student's passport or ID" },
    { text: 'Recent passport-sized photograph' },
    { text: 'Most recent school report card or transcript' },
  ];

  const parentDocuments: DocumentItem[] = [
    { text: "Copy of parent's/guardian's ID or passport" },
    { text: 'Proof of residence in Qatar' },
    { text: 'Emergency contact information' },
    { text: 'Signed terms and conditions form' },
  ];

  const faqs: FAQItem[] = [
    {
      question: 'When can I enroll my child?',
      answer: "We accept enrollments throughout the year, but it's recommended to enroll early to secure preferred time slots and maximize learning benefits.",
    },
    {
      question: 'Can I change subjects after enrollment?',
      answer: "Yes, you can modify your subject selection based on your child's needs. We recommend discussing any changes with our academic team to ensure continuity in learning.",
    },
    {
      question: 'What if my child has special learning needs?',
      answer: 'We work with students of various learning abilities and can provide accommodations where possible. Please discuss your child\'s specific needs during the consultation phase.',
    },
    {
      question: 'What is your refund policy?',
      answer: 'Our refund policy varies based on the program and timing of the withdrawal request. Complete details are provided in our terms and conditions document.',
    },
    {
      question: 'Do you offer make-up classes for missed sessions?',
      answer: 'We offer make-up classes for sessions missed due to illness or emergency, subject to availability and advance notice.',
    },
  ];

  const testimonials: Testimonial[] = [
    {
      name: 'Sivakumar Siva',
      role: 'Parent of Grade 8 student',
      text: 'The enrollment process was smooth and straightforward. The staff took time to understand my child\'s needs and created a perfect learning plan.',
      initials: 'SS',
    },
    {
      name: 'Priya Menon',
      role: 'Parent of Grade 6 student',
      text: 'I appreciated the thorough assessment and consultation. It gave me confidence that my child would receive personalized attention.',
      initials: 'PM',
    },
    {
      name: 'Ahmed Al-Thani',
      role: 'Parent of Grade 10 student',
      text: 'The documentation requirements were clear, and the team helped us through every step. Highly recommend Tuition Point!',
      initials: 'AT',
    },
  ];

  return (
    <div className="bg-accent text-white min-h-screen">
      <SEO
        title="Enroll Now - Admission Process for Tutoring Programs | Tuition Point Qatar"
        description="Start your journey with Tuition Point Qatar. Simple 4-step enrollment process: consultation, assessment, personalized plan, and registration. Flexible scheduling for K-12 students in Al Wakra."
        keywords="enroll tuition Qatar, admission process Al Wakra, tutoring enrollment Qatar, register tuition center, join tutoring program, flexible scheduling Qatar, K-12 admission, enrollment fee, tuition registration, how to enroll"
      />

      {/* Page Header */}
      <section className="pt-16 pb-12 md:pt-20 md:pb-16 bg-accent">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-6 animate-slide-up">
              Enrollment
            </h1>
            <p className="text-lg text-white/90 animate-slide-up">
              Start your journey toward academic excellence with Tuition Point Education Center.
            </p>
          </div>
        </div>
      </section>

      {/* Enrollment Steps */}
      <section className="py-24 bg-accent">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto mb-16 animate-slide-up">
            <h2 className="text-4xl font-display font-semibold text-foreground mb-4">
              Enrollment Process
            </h2>
            <p className="text-lg text-white/80">
              Our simple 4-step enrollment process makes it easy to get started.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {enrollmentSteps.map((step, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg text-foreground p-8 text-center transition-shadow duration-300 hover:shadow-xl animate-slide-up"
              >
                <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  {step.icon}
                </div>
                <h3 className="text-xl font-display font-bold mb-3">{step.title}</h3>
                <p className="text-muted-foreground">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enquiry Form */}
      <section className="py-24 bg-accent">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto mb-16 animate-slide-up">
            <h2 className="text-4xl font-display font-semibold text-white mb-4">
              Submit Your Enquiry
            </h2>
            <p className="text-lg text-white/80">
              Fill out the form below and our team will get back to you within 24 hours.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg text-foreground p-8 md:p-12 animate-slide-up">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Student Name */}
                <div>
                  <label htmlFor="studentName" className="block text-sm font-semibold text-foreground mb-2">
                    Student Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="studentName"
                    name="studentName"
                    value={formData.studentName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-colors"
                    placeholder="Enter student's full name"
                  />
                </div>

                {/* Parent/Guardian Name */}
                <div>
                  <label htmlFor="parentName" className="block text-sm font-semibold text-foreground mb-2">
                    Parent/Guardian Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="parentName"
                    name="parentName"
                    value={formData.parentName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-colors"
                    placeholder="Enter parent's full name"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-colors"
                    placeholder="your.email@example.com"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-foreground mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-colors"
                    placeholder="+974 XXXX XXXX"
                  />
                </div>

                {/* Grade/Class */}
                <div>
                  <label htmlFor="grade" className="block text-sm font-semibold text-foreground mb-2">
                    Grade/Class <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="grade"
                    name="grade"
                    value={formData.grade}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-colors"
                  >
                    <option value="">Select Grade</option>
                    <option value="KG">Kindergarten</option>
                    <option value="1">Grade 1</option>
                    <option value="2">Grade 2</option>
                    <option value="3">Grade 3</option>
                    <option value="4">Grade 4</option>
                    <option value="5">Grade 5</option>
                    <option value="6">Grade 6</option>
                    <option value="7">Grade 7</option>
                    <option value="8">Grade 8</option>
                    <option value="9">Grade 9</option>
                    <option value="10">Grade 10</option>
                    <option value="11">Grade 11</option>
                    <option value="12">Grade 12</option>
                  </select>
                </div>

                {/* Curriculum */}
                <div>
                  <label htmlFor="curriculum" className="block text-sm font-semibold text-foreground mb-2">
                    Curriculum <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="curriculum"
                    name="curriculum"
                    value={formData.curriculum}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-colors"
                  >
                    <option value="">Select Curriculum</option>
                    <option value="CBSE">CBSE</option>
                    <option value="IGCSE">IGCSE</option>
                    <option value="IB">IB (International Baccalaureate)</option>
                    <option value="Federal">Federal Board</option>
                    <option value="Bangladesh">Bangladesh Board</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Subject(s) of Interest */}
                <div className="md:col-span-2">
                  <label htmlFor="subjects" className="block text-sm font-semibold text-foreground mb-2">
                    Subject(s) of Interest
                  </label>
                  <input
                    type="text"
                    id="subjects"
                    name="subjects"
                    value={formData.subjects}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-colors"
                    placeholder="e.g., Mathematics, Physics, Chemistry"
                  />
                </div>

                {/* Message */}
                <div className="md:col-span-2">
                  <label htmlFor="message" className="block text-sm font-semibold text-foreground mb-2">
                    Additional Information
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-colors resize-none"
                    placeholder="Any specific requirements, learning needs, or questions you have..."
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="mt-8">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full md:w-auto bg-accent hover:bg-accent/90 text-white font-semibold py-4 px-12 rounded-lg shadow-lg transition duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <span className="animate-spin">⏳</span>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Submit Enquiry
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Required Documents */}
      <section className="py-24 bg-accent">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto mb-16 animate-slide-up">
            <h2 className="text-4xl font-display font-semibold text-foreground mb-4">
              Required Documents
            </h2>
            <p className="text-lg text-white/80">
              Please prepare the following documents for a smooth enrollment process.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Student Documents */}
            <div className="bg-white rounded-xl shadow-lg text-foreground p-8 animate-slide-up">
              <h3 className="text-2xl font-display font-bold text-foreground mb-6">
                For Students
              </h3>
              <ul className="space-y-4">
                {studentDocuments.map((doc, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">{doc.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Parent Documents */}
            <div className="bg-white rounded-xl shadow-lg text-foreground p-8 animate-slide-up">
              <h3 className="text-2xl font-display font-bold text-foreground mb-6">
                For Parents/Guardians
              </h3>
              <ul className="space-y-4">
                {parentDocuments.map((doc, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">{doc.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-24 bg-accent">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto mb-16 animate-slide-up">
            <h2 className="text-4xl font-display font-semibold text-foreground mb-4">
              Enrollment FAQs
            </h2>
            <p className="text-lg text-white/80">
              Find answers to common questions about our enrollment process.
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg text-foreground p-8 animate-slide-up">
                <h3 className="text-xl font-display font-bold text-foreground mb-4 flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-accent mt-1 flex-shrink-0" />
                  {faq.question}
                </h3>
                <p className="text-muted-foreground ml-9">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-accent">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto mb-16 animate-slide-up">
            <h2 className="text-4xl font-display font-semibold text-foreground mb-4">
              Parent Testimonials
            </h2>
            <p className="text-lg text-white/80">
              Hear from parents who have enrolled their children at Tuition Point.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg text-foreground p-8 animate-slide-up">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-accent fill-accent" />
                  ))}
                </div>
                <p className="text-base text-foreground mb-6 leading-relaxed">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center mr-3 flex-shrink-0">
                    <span className="font-medium text-foreground">{testimonial.initials}</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">{testimonial.name}</h4>
                    <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-accent text-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-display font-semibold mb-6 animate-slide-up">
              Ready to Enroll?
            </h2>
            <p className="text-lg mb-10 text-white text-opacity-90 animate-slide-up">
              Contact us today to begin the enrollment process and take the first step toward academic success.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up">
              <Link
                to="/contact"
                className="bg-white text-accent hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg shadow-xl transition duration-300 sm:w-auto text-lg"
              >
                Contact Us
              </Link>
              <a
                href="tel:+97430158401"
                className="border-2 border-white text-white hover:bg-white hover:text-accent font-semibold py-3 px-8 rounded-lg transition duration-300 sm:w-auto text-lg"
              >
                Call Now
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EnrollmentPage;
